import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { Stack, Typography, Box, useTheme, useMediaQuery } from '@mui/material';
import Grid from '@mui/material/Grid2';
import NoteCard from '@/components/NoteCard/NoteCard';
import NoteListRow from '@/components/NoteListRow/NoteListRow';
import NoteListHeader from '@/components/NoteListHeader/NoteListHeader';
import SkeletonListRow from '@/components/NoteListRow/SkeletonListRow';
import SkeletonCard from '@/components/NoteCard/SkeletonCard';
import useAlertStore from '@/stores/alertStore';
import emptyNotes from '@/assets/empty-notes.png';
import { useAuthStore } from '@/stores/authStore';
import { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { useSearchFilterNotesStore } from '@/stores/searchFilterNotesStore';
import { type NoteSortField } from '@/constants/noteListColumns';
import FilterTags from '@/pages/MyNotes/components/FilterTags/FilterTags';
import DateRangeFilter from '@/pages/MyNotes/components/NotesHeader/DateRangeFilter';
import ViewToggle from '@/pages/MyNotes/components/NotesHeader/ViewToggle';
import ListViewControls from '@/pages/MyNotes/components/NotesHeader/ListViewControls';
import { useBannerStore } from '@/stores/bannerStore';
import { errorReporter } from '@/services/errorReportingService';
import ExpandableSearch from '@/pages/MyNotes/components/NotesHeader/ExpandableSearch';
import { useSavedNotes } from '@/hooks/useSavedNotes';
import { getTagName } from '@/utils/tagUtils';
import { matchesOwner } from '@/helpers/ownerName';
import { mapWithConcurrency, NOTE_FETCH_CONCURRENCY } from '@/utils/mapWithConcurrency';

const NOTES_PER_BATCH = 20;

const SavedNotesContent = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { userData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const {
    searchQuery,
    sortBy,
    sortOrder,
    selectedTags,
    viewMode,
    listViewPreferences,
    dateRange,
    setSortBy,
    setSortOrder,
  } = useSearchFilterNotesStore();

  const { savedNotes, isLoading: isSavedNotesLoading, unsaveNote, toggleNoteFavorite } = useSavedNotes(userData?.id);

  const [notes, setNotes] = useState<NoteOut[]>([]);
  const [tokenMap, setTokenMap] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(NOTES_PER_BATCH);
  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  // Skip refetch after optimistic unsave — the local state is already correct
  const skipNextFetchRef = useRef(false);

  const { headerHeight } = useBannerStore();

  const isListView = viewMode === 'list';

  // Stable identity for "which notes are saved". Changes only when a token is
  // added or removed, never merely because the source array was reallocated.
  const savedTokensKey = useMemo(() => savedNotes.map(({ token }) => token).join('\n'), [savedNotes]);

  // Fetch note data for each saved token
  useEffect(() => {
    if (isSavedNotesLoading) return;

    // After an optimistic unsave, savedNotes changes but local state is already correct
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }

    // Derive the token list from the key, not from `savedNotes`. `useSavedNotes`
    // reallocates that array on ANY write to the user doc — including the
    // unrelated device/browser login write — which re-issued every one of these
    // N requests. Keying on the token *contents* means we only refetch when the
    // set of saved notes actually changes.
    const savedTokens = savedTokensKey ? savedTokensKey.split('\n') : [];

    if (savedTokens.length === 0) {
      setNotes([]);
      setTokenMap({});
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    const fetchAll = async () => {
      const fetchedNotes: NoteOut[] = [];
      const newTokenMap: Record<string, string> = {};
      const staleTokens: string[] = [];

      // There's no batch endpoint, so this is one request per watched note.
      // Bounded rather than a bare Promise.all: a long watched list otherwise
      // opens one XHR per note at once and saturates the browser's connection
      // pool, stalling everything else on the page. Same cap the team-notes
      // view uses.
      await mapWithConcurrency(savedTokens, NOTE_FETCH_CONCURRENCY, async (token) => {
        try {
          const res = await fetch(`${import.meta.env.VITE_API_URL}/shared/${token}`);
          if (!res.ok) {
            if (res.status === 404) staleTokens.push(token);
            return;
          }
          const data = await res.json();
          const noteData: NoteOut = data.note || data;
          if (noteData.id) {
            fetchedNotes.push(noteData);
            newTokenMap[noteData.id] = token;
          }
        } catch (error) {
          errorReporter.expected('Failed to fetch saved shared note', error, {
            feature: 'notes',
            token,
          });
        }
      });

      // Auto-clean stale tokens (expired/deleted shared notes)
      for (const token of staleTokens) {
        unsaveNote(token).catch(() => {});
      }

      if (!cancelled) {
        setNotes(fetchedNotes);
        setTokenMap(newTokenMap);
        setIsLoading(false);
      }
    };

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [savedTokensKey, isSavedNotesLoading, unsaveNote]);

  // The star on a watched note is the VIEWER's, kept on their own user doc
  // beside the token that watches the note — not `note.is_favorite`, which is a
  // single boolean on the note owned by whoever created it (and which the API
  // only lets that owner write). Keyed by note id here because that's what the
  // card and row components speak.
  const favoriteByNoteId = useMemo(() => {
    const byToken = new Map(savedNotes.map(({ token, is_favorite }) => [token, is_favorite]));
    const byNoteId: Record<string, boolean> = {};
    for (const [noteId, token] of Object.entries(tokenMap)) {
      byNoteId[noteId] = byToken.get(token) === true;
    }
    return byNoteId;
  }, [savedNotes, tokenMap]);

  // Toggling a star changes savedNotes but not the set of tokens, so
  // savedTokensKey is unchanged and the note refetch above stays put.
  const handleSaveFavorite = useCallback(
    async (noteId: string, newValue: boolean) => {
      const token = tokenMap[noteId];
      if (!token) return;
      await toggleNoteFavorite(token, newValue);
    },
    [tokenMap, toggleNoteFavorite],
  );

  // Compute tag counts from loaded notes
  const tagCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    notes.forEach((note) => {
      note.tags?.forEach((tag) => {
        const tagName = getTagName(tag);
        if (tagName) {
          const normalized = tagName.trim().toLowerCase();
          counts[normalized] = (counts[normalized] || 0) + 1;
        }
      });
    });
    return counts;
  }, [notes]);

  // Client-side filtering: search, tags, date range, sorting
  const filteredNotes = useMemo(() => {
    let result = notes;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((note) => {
        const titleMatch = note.title?.toLowerCase().includes(query);
        const tagMatch = note.tags?.some((tag) => getTagName(tag).toLowerCase().includes(query));
        return titleMatch || tagMatch || matchesOwner(note.shared_by, query);
      });
    }

    // Tag filter — AND across selected tags, mirroring My Notes (NotesContent)
    if (selectedTags.length > 0) {
      const wanted = selectedTags.map((t) => getTagName(t).toLowerCase());
      result = result.filter((note) =>
        wanted.every((w) => note.tags?.some((tag) => getTagName(tag).toLowerCase() === w)),
      );
    }

    // Date range filter
    if (dateRange.start) {
      const startDate = new Date(dateRange.start + 'T00:00:00');
      result = result.filter((note) => {
        if (!note.created_at) return true;
        return new Date(note.created_at) >= startDate;
      });
    }
    if (dateRange.end) {
      const endDate = new Date(dateRange.end + 'T23:59:59');
      result = result.filter((note) => {
        if (!note.created_at) return true;
        return new Date(note.created_at) <= endDate;
      });
    }

    // Client-side sorting
    const sorted = [...result];
    const asc = sortOrder === 'asc';

    const getMetaValue = (note: NoteOut, field: NoteSortField): number => {
      // The viewer's star, not the note's — see favoriteByNoteId. Sorting by
      // Starred was previously a no-op here because every row was forced false.
      if (field === 'is_favorite') return favoriteByNoteId[note.id] ? 1 : 0;
      const webm = note.metadata?.media?.webm;
      switch (field) {
        case 'duration':
          return webm?.duration ?? -1;
        default:
          return 0;
      }
    };

    if (sortBy === 'title') {
      sorted.sort((a, b) => {
        const ta = (a.title || '').toLowerCase();
        const tb = (b.title || '').toLowerCase();
        return asc ? ta.localeCompare(tb) : tb.localeCompare(ta);
      });
    } else if (sortBy === 'created_at') {
      sorted.sort((a, b) => {
        const da = new Date(a.created_at || 0).getTime();
        const db = new Date(b.created_at || 0).getTime();
        return asc ? da - db : db - da;
      });
    } else {
      sorted.sort((a, b) => {
        const va = getMetaValue(a, sortBy);
        const vb = getMetaValue(b, sortBy);
        if (va === -1 && vb === -1) return 0;
        if (va === -1) return 1;
        if (vb === -1) return -1;
        return asc ? va - vb : vb - va;
      });
    }

    return sorted;
  }, [notes, favoriteByNoteId, searchQuery, selectedTags, dateRange.start, dateRange.end, sortBy, sortOrder]);

  // Progressive rendering
  const visibleNotes = useMemo(() => filteredNotes.slice(0, visibleCount), [filteredNotes, visibleCount]);
  const hasMore = visibleCount < filteredNotes.length;

  useEffect(() => {
    setVisibleCount(NOTES_PER_BATCH);
  }, [searchQuery, sortBy, sortOrder, selectedTags, dateRange, viewMode]);

  const loadMore = useCallback(() => {
    setVisibleCount((prev) => Math.min(prev + NOTES_PER_BATCH, filteredNotes.length));
  }, [filteredNotes.length]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          loadMore();
        }
      },
      { root: gridRef.current, rootMargin: '200px' },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, isLoading, loadMore]);

  const handleUnsave = useCallback(
    async (noteId: string) => {
      const token = tokenMap[noteId];
      if (!token) return;

      // Optimistic removal — update UI instantly, skip the snapshot-triggered refetch
      skipNextFetchRef.current = true;
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      setTokenMap((prev) => {
        const updated = { ...prev };
        delete updated[noteId];
        return updated;
      });

      try {
        await unsaveNote(token);
        showAlert('Note removed from watched list.', 'success');
      } catch {
        skipNextFetchRef.current = false;
        showAlert('Something went wrong. Try again later.', 'error');
      }
    },
    [tokenMap, unsaveNote, showAlert],
  );

  const handleListSortChange = (field: NoteSortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder(field === 'created_at' || field === 'is_favorite' ? 'desc' : 'asc');
    }
  };

  const renderSkeletons = () => {
    if (isListView) {
      return Array.from({ length: 6 }).map((_, index) => (
        <SkeletonListRow key={index} showThumbnails={listViewPreferences.showThumbnails} />
      ));
    }
    return Array.from({ length: 6 }).map((_, index) => <SkeletonCard key={index} />);
  };

  const savedNoteProps = {
    saveFavorite: handleSaveFavorite,
    menuActionLabel: 'Remove from watched',
    deleteModalTitle: 'Confirm remove',
    deleteModalText: 'Remove this note from your watched notes?',
    deleteButtonText: 'Yes, remove',
  };

  const renderNotes = () => {
    if (isLoading) {
      return renderSkeletons();
    }

    if (filteredNotes.length > 0) {
      if (isListView) {
        return visibleNotes.map((note: NoteOut) => (
          <NoteListRow
            key={note.id}
            id={note.id}
            title={note.title}
            tags={note.tags}
            transcript_url={note.transcript_url}
            video_url={note.video_url}
            created_at={note.created_at}
            processing_type={note.processing_type}
            thumbnail_url={note.thumbnail_list_url || note.thumbnail_url}
            creator_user_id={note.creator_user_id}
            shared_by={note.shared_by}
            is_favorite={favoriteByNoteId[note.id] ?? false}
            summary={note.summary}
            metadata={note.metadata}
            showThumbnails={listViewPreferences.showThumbnails}
            showTags={listViewPreferences.showTags}
            onDelete={handleUnsave}
            navigateUrl={`/shared/${tokenMap[note.id]}`}
            {...savedNoteProps}
          />
        ));
      }

      return visibleNotes.map((note: NoteOut) => (
        <NoteCard
          key={note.id}
          noteId={note.id}
          title={note.title || 'Untitled'}
          tags={note.tags}
          transcript={note.transcript_url || ''}
          onConfirmDelete={handleUnsave}
          hasVideo={!!note.video_url}
          createdAt={note.created_at}
          processingType={note.processing_type}
          thumbnailUrl={note.thumbnail_card_url || note.thumbnail_url || ''}
          creatorUserId={note.creator_user_id}
          sharedBy={note.shared_by}
          isFavorite={favoriteByNoteId[note.id] ?? false}
          navigateUrl={`/shared/${tokenMap[note.id]}`}
          {...savedNoteProps}
        />
      ));
    }

    return (
      <Stack width="100%">
        <Grid size={12} marginTop={10} textAlign="center">
          <img width="150px" src={emptyNotes} alt="Illustration showing no watched notes" />
        </Grid>
        <Grid size={12} marginTop={1} textAlign="center">
          <Typography variant="h6" fontWeight={400}>
            {searchQuery ? 'No watched notes match your search.' : "You haven't added any watched notes yet."}
          </Typography>
          {!searchQuery && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              When viewing a shared note, use the bookmark icon to add it to your watched notes.
            </Typography>
          )}
        </Grid>
      </Stack>
    );
  };

  return (
    <Stack
      justifyContent="flex-start"
      maxWidth={isMobile ? '100%' : '1620px'}
      width="100%"
      height={`calc(100% - ${headerHeight}px)`}
      overflow="hidden"
    >
      <Stack
        gap={2}
        sx={{ margin: '26px 0' }}
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
        flexWrap="wrap"
      >
        <Stack flexDirection="row" alignItems="center" gap={1}>
          {isListView && <ListViewControls />}
          <ViewToggle />
          <DateRangeFilter />
          <FilterTags tagCounts={tagCounts} />
          <ExpandableSearch placeholder="Search notes or people..." />
        </Stack>
        {!isLoading && filteredNotes.length > 0 && (
          <NoteListHeader
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={handleListSortChange}
            showThumbnails={isListView && listViewPreferences.showThumbnails}
          />
        )}
      </Stack>

      <Box
        ref={gridRef}
        flex={1}
        minHeight={0}
        pb={2}
        sx={
          isListView
            ? {
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                width: '100%',
                minWidth: 0,
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }
            : {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? 2 : 3,
                alignContent: 'flex-start',
                overflow: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
              }
        }
      >
        {renderNotes()}
        {hasMore && !isLoading && (
          <Box ref={sentinelRef} sx={{ width: '100%', height: 1, flexShrink: 0, gridColumn: '1 / -1' }} />
        )}
      </Box>
    </Stack>
  );
};

export default SavedNotesContent;
